import React, {
  useCallback, useState, useMemo,
} from 'react';
import * as Styles from './ModalPanels.styles';
import { useSelector, useDispatch } from 'react-redux';
import useLocale from '@/hooks/useLocale';
import { useParams } from 'react-router-dom';
import { SelectField } from 'gatorcomponents';
import VpsStoreModal from '@/pages/Products/VpsOciDetailPage/ProductTemplatesPage/commons/VpsStoreModal';
import { getIconBySlug } from '@/pages/Products/VpsOciDetailPage/utils/getIconBySlug';
import { formatCurrency } from '@/utils/Formatters/formatCurrency';
import { vpsOciActions } from '@/redux/modules/productDetail/vpsOci';
import { formatAddonsList } from '@/utils/Formatters/formatAddonsList';

const ModalPanels = ({
  onClose,
  selectedItem,
}) => {
  const dispatch = useDispatch();

  const [modalType, setModalType] = useState('selectionPanels');
  const [previousModalType, setPreviousModalType] = useState(null);
  const [bodyData, setBodyData] = useState(null);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [selectedBillingCycle, setSelectedBillingCycle] = useState(null);

  const vpsOci = useSelector(state => state.vpsOci);
  const { availableOptions, addons } = (vpsOci && vpsOci.data) || {};
  const { panels } = availableOptions || {};

  const { productDetails: productDetailsLocale, billing: billingLocale } = useLocale();
  const { vpsOci: localeVpsOci } = productDetailsLocale;

  const { panels: localeVpsOciStoreDetailsPanels } = localeVpsOci.store && localeVpsOci.store.details;
  const { alert: localeVpsOciStoreModalAlert } = localeVpsOci && localeVpsOci.store && localeVpsOci.store.modal;
  const { panels: localeVpsOciStoreChangeApplicationsModalPanels } = localeVpsOci.store && localeVpsOci.store.changeApplications && localeVpsOci.store.changeApplications.modal;

  const {
    paymentCycle: paymentCycleLocale,
  } = billingLocale;

  const addonsPaidList = useMemo(() => (addons && addons.filter(addon => addon.isPaid)) || [], [addons]);
  const listAddonsNames = useMemo(() => (
    addonsPaidList && addonsPaidList.map(addon => addon.name)
  ), [addonsPaidList]);
  const formattedAddonsText = useMemo(() => formatAddonsList(listAddonsNames), [listAddonsNames]);
  const hasPaidAddons = useMemo(() => (addonsPaidList && addonsPaidList.length > 0), [addonsPaidList]);

  const selectedPanels = useMemo(() => panels && panels.find(panel => selectedItem && panel.slug === selectedItem),
    [panels, selectedItem]);

  const panelAddonsList = selectedPanels && selectedPanels.addons;
  const hasAddonsForPanel = panelAddonsList && panelAddonsList.length > 0 && panelAddonsList.some(addon => (addon.seats > 0 && (addon.billingcycles && addon.billingcycles.length > 0)));

  const provisionedSo = selectedPanels && selectedPanels.operationalSystems && selectedPanels.operationalSystems.name;

  const {
    name: panelName = '',
    slug: panelslug = null,
  } = selectedPanels || {};

  const disableSelectionPanelsConfirmButton = (!selectedAccountId || !selectedBillingCycle) && hasAddonsForPanel;

  const { id: hostingId } = useParams();

  const getDescriptionBySlug = useCallback((slug) => {
    const item = localeVpsOciStoreDetailsPanels[slug];
    return item ? item.description : null;
  }, [localeVpsOciStoreDetailsPanels]);

  const handleCloseModal = useCallback(() => {
    onClose && onClose();
    setBodyData(null);
    setSelectedAccountId(null);
    setSelectedBillingCycle(null);
  }, [onClose]);

  const handleChangeAccountSeats = useCallback((selectedOption) => {
    setSelectedAccountId(selectedOption.value);
    setSelectedBillingCycle(null);
  }, []);

  const handleChangeAccountFrequency = useCallback((selectedOption) => {
    setSelectedBillingCycle(selectedOption.value);
  }, []);

  const handleBackStep = useCallback(() => {
    setModalType(previousModalType);
    setBodyData(null);
  }, [previousModalType]);

  const handleStepSelectAccount = useCallback(() => {
    const addonId = (selectedPanels && selectedPanels.addons && selectedPanels.addons.length > 0) && selectedPanels.addons[0].id;
    const newImageId = selectedAccountId || addonId;

    setBodyData({
      hostingId,
      newImageId,
      imageType: 'panel',
      billingCycle: selectedBillingCycle,
    });

    setPreviousModalType(modalType);

    if (hasPaidAddons) {
      setModalType('confirmWithPanelPaidActive');
      return;
    }
    setModalType('confirmWithoutPanelPaidActive');
  }, [modalType, selectedAccountId, selectedBillingCycle, hasPaidAddons, hostingId]);

  const handleStepConfirm = useCallback(() => {
    if (hostingId) {
      dispatch(vpsOciActions.changeImage.request(bodyData));
    }
    handleCloseModal();
  }, [handleCloseModal, bodyData, hostingId, dispatch]);

  const modalPanelsDetails = useMemo(() => {
    const icon = panelslug ? getIconBySlug(panelslug) : null;
    const description = panelslug ? getDescriptionBySlug(panelslug) : null;

    const selectedSOInfoData = {
      name: panelName,
      description: description || '',
      icon: icon ? (
        <Styles.SelectedPanelTitleIcon
          src={icon}
          alt={panelName}
          data-testid={`vpsoci-templates-panel-icon-${panelslug}`}
        />
      ) : null,
    };

    if (!selectedSOInfoData) return null;

    const optionsAccountsList = hasAddonsForPanel
      ? panelAddonsList.map(addon => ({
        label: `${addon.name} - ${localeVpsOciStoreChangeApplicationsModalPanels.qtyAccountsText(addon.seats)}`,
        value: addon.id,
      }))
      : null;

    const selectedAddonBillingcycles = hasAddonsForPanel
      ? panelAddonsList.find(addon => addon.id === selectedAccountId)
      : null;

    const optionsPaymentFrequencyList = (selectedAddonBillingcycles && selectedAddonBillingcycles.billingcycles)
      ? selectedAddonBillingcycles.billingcycles.map(frequency => ({
        label: `${paymentCycleLocale[frequency.cycle]} (${frequency.currencyPrefix} ${formatCurrency(frequency.price)})`,
        value: frequency.cycle,
      }))
      : null;

    if (!selectedSOInfoData) return null;

    return (
      <Styles.SelectedPanel data-testid="vpsoci-templates-panel">
        <Styles.SelectedPanelTitle data-testid="vpsoci-templates-panel-name">
          {selectedSOInfoData.icon}
          {selectedSOInfoData.name}
        </Styles.SelectedPanelTitle>
        <Styles.SelectedPanelDescription data-testid="vpsoci-templates-panel-description">
          {selectedSOInfoData.description}

          {localeVpsOciStoreChangeApplicationsModalPanels.provisioned && (
            <Styles.ProvisionedText>
              {localeVpsOciStoreChangeApplicationsModalPanels.provisioned(provisionedSo)}
            </Styles.ProvisionedText>
          )}
        </Styles.SelectedPanelDescription>

        {optionsAccountsList && (
          <Styles.SelectWrapper>
            <SelectField
              placeholder={localeVpsOciStoreChangeApplicationsModalPanels.placeholder.optionsAccounts}
              options={optionsAccountsList}
              onClickSelect={option => handleChangeAccountSeats(option)}
              withScroll
              filterable
            />
          </Styles.SelectWrapper>
        )}

        {optionsPaymentFrequencyList && (
          <Styles.SelectWrapper>
            <SelectField
              key={`payment-frequency-${selectedAccountId}`}
              placeholder={localeVpsOciStoreChangeApplicationsModalPanels.placeholder.paymentFrequency}
              options={optionsPaymentFrequencyList}
              clearValue={selectedBillingCycle === null}
              onClickSelect={option => handleChangeAccountFrequency(option)}
              withScroll
              filterable
            />
          </Styles.SelectWrapper>
        )}

      </Styles.SelectedPanel>
    );
  }, [
    panelslug,
    getDescriptionBySlug,
    panelName,
    hasAddonsForPanel,
    panelAddonsList,
    localeVpsOciStoreChangeApplicationsModalPanels,
    paymentCycleLocale,
    provisionedSo,
    selectedAccountId,
    selectedBillingCycle,
    handleChangeAccountSeats,
    handleChangeAccountFrequency,
  ]);

  const modalConfig = useMemo(() => ({
    selectionPanels: {
      title: localeVpsOciStoreChangeApplicationsModalPanels.title,
      message: modalPanelsDetails,
      alert: null,
      testId: 'vpsoci-panels-confirm-modal',
      confirmButton: {
        text: localeVpsOciStoreChangeApplicationsModalPanels.selectionPanels.confirmButton,
        action: handleStepSelectAccount,
        disabled: disableSelectionPanelsConfirmButton,
        testId: 'vpsoci-panels-confirm-modal-confirm-button',
      },
      closeButton: {
        text: localeVpsOciStoreChangeApplicationsModalPanels.selectionPanels.closeButton,
        action: handleCloseModal,
        testId: 'vpsoci-panels-confirm-modal-close-button',
      },
    },

    confirmWithoutPanelPaidActive: {
      title: localeVpsOciStoreChangeApplicationsModalPanels.title,
      message: localeVpsOciStoreChangeApplicationsModalPanels.confirmWithoutPanelPaidActive.message(panelName),
      alert: localeVpsOciStoreModalAlert.backup,
      agreedTerms: localeVpsOciStoreChangeApplicationsModalPanels.confirmWithoutPanelPaidActive.agreedTerms(panelName),
      testId: 'vpsoci-panels-paid-confirm-modal',
      confirmButton: {
        text: localeVpsOciStoreChangeApplicationsModalPanels.confirmWithoutPanelPaidActive.confirmButton,
        action: handleStepConfirm,
        testId: 'vpsoci-panels-paid-confirm-modal-confirm-button',
      },
      closeButton: {
        text: localeVpsOciStoreChangeApplicationsModalPanels.confirmWithoutPanelPaidActive.closeButton,
        action: handleBackStep,
        testId: 'vpsoci-panels-paid-confirm-modal-close-button',
      },
    },
    confirmWithPanelPaidActive: {
      title: localeVpsOciStoreChangeApplicationsModalPanels.title,
      message: localeVpsOciStoreChangeApplicationsModalPanels.confirmWithPanelPaidActive.message(panelName, formattedAddonsText),
      alert: localeVpsOciStoreModalAlert.backup,
      agreedTerms: localeVpsOciStoreChangeApplicationsModalPanels.confirmWithPanelPaidActive.agreedTerms(panelName, formattedAddonsText),
      testId: 'vpsoci-panels-paid-confirm-modal',
      confirmButton: {
        text: localeVpsOciStoreChangeApplicationsModalPanels.confirmWithPanelPaidActive.confirmButton,
        action: handleStepConfirm,
        testId: 'vpsoci-panels-paid-confirm-modal-confirm-button',
      },
      closeButton: {
        text: localeVpsOciStoreChangeApplicationsModalPanels.confirmWithPanelPaidActive.closeButton,
        action: handleBackStep,
        testId: 'vpsoci-panels-paid-confirm-modal-close-button',
      },
    },
  }), [
    localeVpsOciStoreChangeApplicationsModalPanels,
    modalPanelsDetails,
    handleStepSelectAccount,
    handleCloseModal,
    localeVpsOciStoreModalAlert,
    handleStepConfirm,
    handleBackStep,
    formattedAddonsText,
    panelName,
    disableSelectionPanelsConfirmButton,
  ]);

  if (!selectedItem || !panels || Object.keys(panels).length === 0) return null;

  return (
    <VpsStoreModal
      modalType={modalType}
      modalConfig={modalConfig}
      onClose={handleCloseModal}
    />
  );
};

export default ModalPanels;
